'use client';

import * as React from 'react';
import type { CityCode } from '@loci/city-editions';

import { cn } from '@/components/atoms/primitives/utils';
import { GLOBAL_NAV_ACCENT_VARS } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import type { CityHeaderActiveNav } from '@/components/molecules/CityHeader/cityHeaderShared';
import {
  CityMenuSheetContent,
  NavMenuLockup,
  NavMenuSheet,
  NavMenuTrigger,
} from '@/components/molecules/NavMenuSheet';
import { useCitySignInHref } from '@/hooks/useCitySignInHref';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import type { CategoryTonightCounts } from '@/hooks/useCategoryTonightCounts';
import { useCityIsStaging } from '@/lib/city-context.client';
import { cityHostLabel, defaultCityCode } from '@/lib/cross-host-urls';

/**
 * CityNavMenu — the marketing-city **host** for the surface-neutral
 * `NavMenuSheet` shell (WEB-613 → WEB-629 → WEB-614, per the DES-60 rescope).
 *
 * Everything city-shaped lives here, on the consumer side of the seam: the
 * city name, the `CityCode` → host eyebrow resolution, the lockup, the `city`
 * accent layer, the open state that its own trigger toggles, and the
 * `CityMenuSheetContent` body (hubs, secondary nav, Wayfinder, auth). The
 * shell itself stays surface-neutral so `AppNavMenu` can mount it with app
 * chrome instead.
 *
 * FRE-1389 (DS 1.9.1): the hamburger is a **width** rule, not an auth rule.
 * It renders for every visitor — the sitemap is the only route to the city
 * hubs once the primary row hides — and the signed-in visitor's identity,
 * Notifications and Sign out live in the avatar's account drawer
 * (`CityAccountMenu`) instead. `authUser` / `isAuthPending` only decide
 * whether the sitemap closes with the anon sign-up block (WEB-640).
 *
 * Island pattern — `'use client'`, mounted from the otherwise server-default
 * `CityHeader` (same as `WayfinderSheet` / `CityHeaderSearch`), so the header
 * tree is never opted into the client bundle.
 *
 * FRE-1362 (reverses the prior "renders at every width" guidance, WEB-629):
 * the sitemap-menu takeover replaced the retired city dock ≤720px only
 * (design PL-02) — the desktop primary nav (`CityHeader`'s `<nav>`) is the
 * hub path from 721px up. The trigger carries `city-nav-menu-trigger`, hidden
 * ≥721px by `styles/components/nav-lockup.css` (it beats the `GlassControl`
 * atom's `.a-btn` `inline-flex`, which `AppNavMenu`'s trigger also relies on).
 */

const DEFAULT_LOCKUP_SRC = '/brand/loci-lettermark.svg';

export interface CityNavMenuProps {
  /** City display name for the lockup alt text + city label. */
  cityName: string;
  /** City code for the coord-strip host eyebrow. Defaults to the registry default. */
  cityCode?: CityCode;
  /** Path to the city lockup SVG. Defaults to the national amber lettermark. */
  lockupSrc?: string;
  /** Which primary hub is the current page (or `null`). */
  activeNav?: CityHeaderActiveNav;
  /**
   * Signup destination; `from=city-nav` is appended in the content body.
   * Defaults to cross-host signup with `next=<this page>` (FRE-1389).
   */
  signUpUrl?: string;
  /** Sign-in destination for the menu auth block. */
  signInUrl?: string;
  /**
   * Signed-in identity (WEB-640). When set, the sitemap drops the anon
   * sign-up block — identity lives in the account drawer (FRE-1389).
   */
  authUser?: CityAuthUser | null;
  /**
   * Cookie/hint-armed probe still settling with no identity yet (WEB-640).
   * Suppresses the anon conversion CTA until `/auth/me` resolves.
   */
  isAuthPending?: boolean;
  /**
   * Tonight counts seed for the Categories chip group (WEB-1180). Omit to
   * fetch when the takeover opens; `null` renders the chips without counts.
   */
  categoryTonightCounts?: CategoryTonightCounts | null;
  /** Uncontrolled initial open — Storybook / tests. */
  defaultOpen?: boolean;
  /** Extra classes on the trigger button. */
  className?: string;
}

export function CityNavMenu({
  cityName,
  cityCode = defaultCityCode(),
  lockupSrc = DEFAULT_LOCKUP_SRC,
  activeNav = null,
  signUpUrl,
  signInUrl,
  authUser = null,
  isAuthPending = false,
  categoryTonightCounts,
  defaultOpen = false,
  className,
}: CityNavMenuProps): React.ReactElement {
  const isStaging = useCityIsStaging();
  const resolvedSignInUrl = useCitySignInHref(cityCode, signInUrl, { isStaging });
  const [open, setOpen] = React.useState(defaultOpen);
  // When Search or Wayfinder opens another surface in the same gesture as
  // menu dismiss, skip returning focus to the hamburger (WEB-614 / WEB-639).
  // Reset to true whenever the menu opens again.
  const [restoreFocusOnClose, setRestoreFocusOnClose] = React.useState(true);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleOpenChange = React.useCallback((next: boolean) => {
    setOpen(next);
    if (next) setRestoreFocusOnClose(true);
  }, []);

  const handleBeforeOpenOverlay = React.useCallback(() => {
    setRestoreFocusOnClose(false);
  }, []);

  return (
    <>
      <NavMenuTrigger
        ref={triggerRef}
        isOpen={open}
        onClick={() => handleOpenChange(true)}
        data-testid="city-menu-trigger"
        className={cn('city-nav-menu-trigger', className)}
      />

      <NavMenuSheet
        open={open}
        onOpenChange={handleOpenChange}
        triggerRef={triggerRef}
        restoreFocusOnClose={restoreFocusOnClose}
        title={`${cityName} menu`}
        description={`Site navigation and account actions for ${cityName}.`}
        eyebrow={`CITY · ${cityHostLabel(cityCode, { isStaging }).toUpperCase()}`}
        accentVars={GLOBAL_NAV_ACCENT_VARS.city}
        chromeStart={
          <NavMenuLockup src={lockupSrc} alt={`LOCI ${cityName} lockup`} label={cityName} />
        }
      >
        <CityMenuSheetContent
          cityCode={cityCode}
          cityName={cityName}
          activeNav={activeNav}
          signUpUrl={signUpUrl}
          signInUrl={resolvedSignInUrl}
          authUser={authUser}
          isAuthPending={isAuthPending}
          categoryTonightCounts={categoryTonightCounts}
          onBeforeOpenSearch={handleBeforeOpenOverlay}
          onBeforeOpenWayfinder={handleBeforeOpenOverlay}
        />
      </NavMenuSheet>
    </>
  );
}
